import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { qboReconciliationEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { ReconciliationReport, ResolveMismatchAction } from '@/types/qbo-reconciliation';

export interface ResolveMismatchVars {
  reportId: string;
  mismatchId: string;
  action: ResolveMismatchAction;
  notes?: string;
}

interface ResolveResponse {
  report: ReconciliationReport;
}

/**
 * Spec §9.6 — Apply an operator action (`pull_qbo` | `push_notifi` |
 * `ignore`) to a single mismatch. Backend records an audit entry and
 * re-emits the patched report so the UI replaces its cached snapshot
 * without a refetch.
 */
export function useResolveMismatch() {
  const queryClient = useQueryClient();
  return useApiMutation<ResolveResponse, ResolveMismatchVars>({
    mutationFn: ({ reportId, mismatchId, action, notes }) =>
      apiPost<ResolveResponse, { action: ResolveMismatchAction; notes?: string }>(
        operationsClient,
        qboReconciliationEndpoints.resolveMismatch(reportId, mismatchId),
        notes ? { action, notes } : { action },
      ),
    onSuccess: (data) => {
      // Update the latest-report cache directly so the table updates
      // without an extra round-trip.
      queryClient.setQueryData(settingsKeys.qboReconciliationLatest(), { report: data.report });
      queryClient.setQueryData(settingsKeys.qboReconciliationReport(data.report.id), {
        report: data.report,
      });
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.qboDeadLetter(),
      });
      toast.success(actionLabel(data.report.status));
    },
  });
}

function actionLabel(status: ReconciliationReport['status']): string {
  return status === 'RESOLVED' ? 'All mismatches resolved' : 'Mismatch resolved';
}
